import { useEffect, useState } from "react";

/**
 * Detecta PWAs instalados ANTES do manifest tenant-aware entrar em produção.
 * Esses atalhos foram criados com `start_url: /` e, uma vez persistidos no
 * iOS Safari, não atualizam o start_url mesmo que o manifest mude no servidor.
 *
 * Heurística:
 *   - standalone (ou iOS standalone)
 *   - E a URL atual acabou de entrar em `/` ou `/entrar` (não em `/{slug}/`)
 *
 * Mostra um aviso amistoso com passo a passo para remover e reinstalar.
 * Persiste dispensa por 24h em localStorage.
 */
const DISMISS_KEY = "fg-stale-pwa-dismissed-until";

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone = typeof navigator !== "undefined" && navigator.standalone === true;
  return mm || iosStandalone;
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export default function StalePWABanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    try {
      // Só considera "stale" se o PWA abriu em / ou /entrar (sem slug)
      const path = window.location.pathname || "/";
      const firstSeg = path.split("/").filter(Boolean)[0] || "";
      const openedAtRoot = firstSeg === "" || firstSeg === "entrar";
      if (!openedAtRoot) return;
      if (!isStandalone()) return;
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (until && Date.now() < until) return;
      setVisible(true);
    } catch { /* localStorage bloqueado — não mostra */ }
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000)); } catch {}
    setVisible(false);
  };

  const ios = isIOS();

  return (
    <div
      data-testid="stale-pwa-banner"
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-lg shadow-amber-900/10"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 h-8 w-8 shrink-0 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-lg">!</div>
        <div className="flex-1 text-sm text-amber-900">
          <p className="font-semibold">Seu atalho está desatualizado</p>
          <p className="mt-1 text-amber-800/90">
            Esta instalação do FILA foi feita antes da atualização e por isso abre no site geral em vez do seu salão.
            Para corrigir, remova este ícone e instale de novo pelo endereço do seu salão.
          </p>
          {expanded && (
            <div className="mt-3 rounded-xl bg-white border border-amber-200 p-3 text-[13px] leading-5 text-amber-900">
              {ios ? (
                <ol className="list-decimal pl-4 space-y-1">
                  <li>Pressione e segure o ícone do FILA na tela de início.</li>
                  <li>Toque em <strong>Remover App</strong> → <strong>Excluir da Tela de Início</strong>.</li>
                  <li>Abra o <strong>Safari</strong> e vá para <strong>filagestao.com.br/lerose/login</strong>.</li>
                  <li>Toque em <strong>Compartilhar</strong> (ícone ↑) → <strong>Adicionar à Tela de Início</strong>.</li>
                  <li>Abra pelo novo ícone — ele já entra direto no Le Rosê.</li>
                </ol>
              ) : (
                <ol className="list-decimal pl-4 space-y-1">
                  <li>Pressione e segure o ícone do FILA e toque em <strong>Desinstalar</strong>.</li>
                  <li>Abra o <strong>Chrome</strong> em <strong>filagestao.com.br/lerose/login</strong>.</li>
                  <li>Toque em ⋮ (menu) → <strong>Instalar app</strong>.</li>
                  <li>Abra pelo novo ícone — ele já entra direto no Le Rosê.</li>
                </ol>
              )}
            </div>
          )}
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              data-testid="btn-stale-pwa-expand"
              onClick={() => setExpanded((v) => !v)}
              className="rounded-full bg-amber-900 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white"
            >
              {expanded ? "Ocultar passos" : "Como corrigir"}
            </button>
            <button
              type="button"
              data-testid="btn-stale-pwa-dismiss"
              onClick={dismiss}
              className="text-xs text-amber-900/70 underline"
            >
              Lembrar amanhã
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
